<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useFilters, useOrderExpenses, useOrderPayments } from "@/composables";
import { getEntityKey } from "@/utils/sameEntity";

import type { OrderExpense, OrderPayment } from "@/api/orders/dto/order.dto";

const props = defineProps<{
  orderPayments: OrderPayment[];
  orderExpenses: OrderExpense[];
}>();

const emits = defineEmits<{
  (e: "onOpenFinances"): void;
}>();

const { t } = useI18n();
const { mdash } = useFilters();
const { formatOrderPaymentRate, isDeductionPaymentType } = useOrderPayments();
const { formatOrderExpenseRate } = useOrderExpenses();

const sections = computed(() => {
  const paymentRows = props.orderPayments.map((payment) => {
    return {
      key: `payment-${getEntityKey(payment)}`,
      type: payment.type ? t(`order-payments.${payment.type}`) : mdash(null),
      rate: payment.rate === null ? mdash(null) : formatOrderPaymentRate(payment.rate),
      isNegative: isDeductionPaymentType(payment.type),
    };
  });

  const expenseRows = props.orderExpenses.map((expense) => {
    return {
      key: `expense-${expense.id}`,
      type: t(`order-expenses.${expense.type}`),
      rate: formatOrderExpenseRate(-expense.rate),
      isNegative: true,
    };
  });

  return [
    { key: "payments", title: t("payments.order-payments"), rows: paymentRows },
    { key: "expenses", title: t("payments.expenses"), rows: expenseRows },
  ].filter((section) => section.key === "payments" || section.rows.length > 0);
});
</script>

<template>
  <v-menu location="bottom start" offset="4" width="320">
    <template #activator="{ props: menuProps, isActive }">
      <span class="cursor-pointer" v-bind="menuProps">
        <slot :is-active="isActive" name="activator"></slot>
      </span>
    </template>

    <div class="bg-white rounded order-money-popover">
      <template v-for="section in sections" :key="section.key">
        <div class="px-4 pt-4 pb-3 font-size-12 font-weight-500 text-grey-darken-3">
          {{ section.title }}
        </div>

        <div
          :data-qa="`order-money-popover-${section.key}`"
          class="d-flex flex-column px-4 pb-3 text-grey-darken-3 order-money-popover__rows"
        >
          <span v-if="!section.rows.length" data-qa="order-money-popover-empty">
            {{ t("payments.no-active-payments") }}
          </span>
          <div
            v-for="row in section.rows"
            :key="row.key"
            class="d-flex align-center order-money-popover__row"
            data-qa="order-money-popover-row"
          >
            <span class="flex-grow-1">{{ row.type }}</span>
            <span
              :class="{
                'text-error': row.isNegative,
              }"
              class="text-right order-money-popover__rate"
              >{{ row.rate }}</span
            >
          </div>
        </div>

        <v-divider />
      </template>

      <div class="pa-2">
        <v-btn
          class="text-none font-weight-regular"
          color="primary"
          data-qa="order-money-popover-open-finances"
          variant="text"
          @click="emits('onOpenFinances')"
        >
          <span>
            {{ t("payments.open-finances-tab") }}
          </span>
        </v-btn>
      </div>
    </div>
  </v-menu>
</template>

<style lang="scss" scoped>
.order-money-popover {
  box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.16);

  &__rows {
    gap: 10px;
  }

  &__row {
    gap: 8px;
  }

  &__rate {
    width: 90px;
  }
}
</style>
